import React, { memo } from 'react';
import { ClipboardCheckIcon, BookOpenIcon, TrophyIcon, CheckIcon, LockIcon, ShieldIcon, WifiIcon, SmartphoneIcon, CreditCardIcon, EyeOffIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function HowItWorksPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-2153" data-mp="1kdxm2a8r6ph3">
      <TopBar data-id="element-2154" data-mp="1fdt2ucggngn6" />
      <Header data-id="element-2155" data-mp="1feb5bqtpur3j" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-2156" data-mp="1ffcko7hrlzr2">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2157" data-mp="1ubtqkj76qm68">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2158" data-mp="1pc8r87o3sbsz">
            <h1 className="mb-6 text-5xl md:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-2159" data-mp="1panzept6b6yt">
              From "I know we should do something" to actually done.
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-2160" data-mp="30qmcr5vdwf5u">
              Most families never get around to the basics — not because they
              don't care, but because nothing makes it easy. Caveo walks every
              person through the actions that matter, checks that they're done,
              and keeps everyone on track.
            </p>
          </div>
        </div>
      </section>

      {/* The 3-Step Journey */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-2161" data-mp="1a5wv9dxq5ncr">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2162" data-mp="1a7hpt3k1h6am">
          <div className="text-center mb-16" data-id="element-2163" data-mp="1f3yh26mjc0rx">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-2164" data-mp="1f2nc43hcsmz8">
              Here's how Caveo gets your family protected — and keeps them that
              way.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-2165" data-mp="1nyw795yaszfk">
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-2166" data-mp="rex1lzfabpd5">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-2167" data-mp="vexsijjklt13">
                1
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-2168" data-mp="1tqh0bsadt3v0">
                <ClipboardCheckIcon className="w-10 h-10 text-[#631bb7]" data-id="element-2169" data-mp="23jpmpjx605uv" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-2170" data-mp="1ftlfz3oojpzn">
                See What's Been Missed
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2171" data-mp="1avtqegbicfym">
                A few plain-English questions about your household reveals
                exactly what hasn't been done and what matters most. No
                technical knowledge needed to answer them.
              </p>
              <ul className="text-left space-y-2" data-id="element-2172" data-mp="306u7bcv3f1fa">
                <li className="flex items-center text-sm text-gray-500" data-id="element-2173" data-mp="2w7lfidjm5dmd">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-2174" data-mp="33qkvsabs2p1i" />
                  Takes 3 minutes. No jargon. No scoring system to decode.
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-2175" data-mp="2tsew7zdg1uzt">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-2176" data-mp="2yri3zrkt6fbh">
                2
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-2177" data-mp="3rfvy3w8s0rhr">
                <BookOpenIcon className="w-10 h-10 text-[#631bb7]" data-id="element-2178" data-mp="3cp6k6m3xkya7" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-2179" data-mp="3hoj5h2f5lxao">
                Do What Matters
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2180" data-mp="e194vr6zft1l">
                Caveo walks each family member through the actions that actually
                protect them — step by step, in plain English, until they're
                done. Not just informed. Done.
              </p>
              <ul className="text-left space-y-2" data-id="element-2181" data-mp="2hlbly7zkwwjt">
                <li className="flex items-center text-sm text-gray-500" data-id="element-2182" data-mp="2cl770mebk9vp">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-2183" data-mp="27lb1ujtvycek" />
                  Every guide is built for people who aren't technical — and
                  most take under 15 minutes.
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-2184" data-mp="30a8gmcyw1r2k">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-2185" data-mp="2vh71q4yolriz">
                3
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-2186" data-mp="3o1e0d37y93i5">
                <TrophyIcon className="w-10 h-10 text-[#631bb7]" data-id="element-2187" data-mp="3rijtluiud9fl" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-2188" data-mp="3vjvbfdvh4qd8">
                Stay Protected
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2189" data-mp="4axyx70gcpye">
                Caveo doesn't stop when the setup is done. Ongoing check-ins and
                coordinated reminders make sure safe habits stick across your
                whole household — not just yours.
              </p>
              <ul className="text-left space-y-2" data-id="element-2190" data-mp="iaz9xfafldrl">
                <li className="flex items-center text-sm text-gray-500" data-id="element-2191" data-mp="n30m717ln4hm">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-2192" data-mp="n53iu439bvvb" />
                  One family dashboard keeps everyone accountable — without you
                  having to manage it yourself.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The 3-Level Education System */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-2193" data-mp="cbm6uf6yl3ui">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2194" data-mp="7davg010mla6">
          <div className="text-center mb-16" data-id="element-2195" data-mp="7evqdqr8s68k">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-2196" data-mp="m1ozgea64fsb">
              39 guided actions. Every critical thing your family hasn't done
              yet.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2197" data-mp="v2dcb5ntsa3m">
              Caveo covers what matters most — from password protection to
              financial account security to knowing what to do when something
              goes wrong. Every guide is a step-by-step walkthrough, not a
              lesson to sit through.
            </p>
          </div>

          {/* Level 1 */}
          <div className="mb-12" data-id="element-2198" data-mp="36s68pysqft9p">
            <div className="flex items-center mb-6" data-id="element-2199" data-mp="36t7j011xcbqd">
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-2200" data-mp="36u0qx5cmd7a3">
                Start Here — The actions that protect most people most of the
                time
              </div>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-2201" data-mp="1m9lx8bypzjz5">
              The fundamentals everyone needs. Perfect for beginners or anyone
              wanting to solidify their basics.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2202" data-mp="1cj10ku2lwpiv">
              {[{
              icon: LockIcon,
              title: 'Creating Bulletproof Passwords',
              desc: 'Learn to create and manage strong passwords without memorizing them all'
            }, {
              icon: ShieldIcon,
              title: 'Recognizing Scams & Phishing',
              desc: 'Spot fake emails, texts, and calls before they trick you'
            }, {
              icon: WifiIcon,
              title: 'Securing Your Home WiFi',
              desc: 'Protect your network from unwanted visitors'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-2203" data-mp="16qiys4xqnqxs" data-mpi={index}>
                  <module.icon className="w-6 h-6 text-green-600 mb-3" data-id="element-2204" data-mp="11yhsczn94ufo" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-2205" data-mp="x06dubm0hvr4">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-2206" data-mp="s3g19y9f1iag">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 2 */}
          <div className="mb-12" data-id="element-2207" data-mp="j2hvbaff30p8">
            <div className="flex items-center mb-6" data-id="element-2208" data-mp="j1qb0vr9spe9">
              <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-2209" data-mp="e35aswc0q5pt">
                Go Deeper — For the things most families overlook
              </div>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-2210" data-mp="28gxutjrvxdc2">
              Build on your foundation with more advanced protection strategies.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2211" data-mp="2kz0hp5foh31a">
              {[{
              icon: CreditCardIcon,
              title: 'Protecting Financial Accounts',
              desc: 'Secure your bank accounts, credit cards, and investment apps'
            }, {
              icon: SmartphoneIcon,
              title: 'Mobile Device Security',
              desc: 'Keep your phone and tablet safe from threats'
            }, {
              icon: EyeOffIcon,
              title: 'Privacy Settings Deep Dive',
              desc: 'Take control of what apps and websites know about you'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-2212" data-mp="3iduylktcwd6l" data-mpi={index}>
                  <module.icon className="w-6 h-6 text-blue-600 mb-3" data-id="element-2213" data-mp="3kvmvltsgju6z" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-2214" data-mp="3kt90jkxs2hzf">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-2215" data-mp="3ukd4zqptukg0">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 3 */}
          <div data-id="element-2216" data-mp="3gok2ilrx7ii">
            <div className="flex items-center mb-6" data-id="element-2217" data-mp="31w9037f2ybed">
              <div className="bg-[#631bb7] bg-opacity-20 text-[#631bb7] px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-2218" data-mp="31xsdmq9cq8cg">
                For the Thorough Ones — Complete coverage, including the things
                you haven't thought of yet
              </div>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-2219" data-mp="3gv2xoo028mjs">
              Expert-level topics for those who want comprehensive protection.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-2220" data-mp="w39b1hyc7hlj">
              {['AI Safety Primer', 'Digital Legacy Planning', 'Identity Masking', 'Cloud Storage Security', 'Smart Home Protection', 'Travel Security', 'Social Media Privacy', 'Data Broker Removal'].map((topic, index) => <div key={index} className="bg-[#f8f8f8] rounded-lg p-4 border border-gray-200 text-center" data-id="element-2221" data-mp="21vdsr1lvbamb" data-mpi={index}>
                  <span className="text-sm font-medium text-[#111111]" data-id="element-2222" data-mp="1svz6kecqhm7m">
                    {topic}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Module Preview */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-2223" data-mp="1ize0rqsw472q">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2224" data-mp="3ia5dcscrvdl2">
          <div className="text-center mb-12" data-id="element-2225" data-mp="3db2k0565mvsx">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-2226" data-mp="3dcdltzg1hthe">
              Here's what a Caveo walkthrough looks like.
            </h2>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-2227" data-mp="1ge6k7a7m3m2c">
            {/* Module Header */}
            <div className="bg-[#1a0647] p-6" data-id="element-2228" data-mp="11m4gff2f88x1">
              <div className="flex items-center justify-between" data-id="element-2229" data-mp="16miq11jo7qx6">
                <div data-id="element-2230" data-mp="1ucwgwoqirjq6">
                  <span className="text-[#00f4f8] text-sm font-semibold" data-id="element-2231" data-mp="1uccw909hih8g">
                    Start Here
                  </span>
                  <h3 className="text-white text-xl font-bold mt-1" data-id="element-2232" data-mp="1zao6hc3oytr7">
                    Recognizing Scams & Phishing
                  </h3>
                </div>
                <div className="text-white text-right" data-id="element-2233" data-mp="1pda4ghiim7if">
                  <span className="text-sm opacity-70" data-id="element-2234" data-mp="1pfwc0h5eon0n">Progress</span>
                  <div className="text-lg font-bold" data-id="element-2235" data-mp="8ys2ftabt9fc">3 of 8 steps</div>
                </div>
              </div>
              <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2" data-id="element-2236" data-mp="e07p1ps5jnzu">
                <div className="bg-[#00f4f8] h-2 rounded-full" style={{
                width: '37.5%'
              }} data-id="element-2237" data-mp="2s5cn4gxeep81"></div>
              </div>
            </div>

            {/* Module Content */}
            <div className="p-8" data-id="element-2238" data-mp="2x5iia72vmtuv">
              <h4 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-2239" data-mp="2x58nm66vzyiu">
                Step 3: Check the sender's email address
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-2240" data-mp="29dtmuhurdp9p">
                Scammers often use email addresses that look similar to real
                companies but have small differences. Here's how to spot them:
              </p>

              {/* Visual Example */}
              <div className="bg-[#f8f8f8] rounded-xl p-6 mb-6 border-2 border-gray-200" data-id="element-2241" data-mp="2dfydd7pwu5s4">
                <p className="text-sm font-semibold text-gray-500 mb-4" data-id="element-2242" data-mp="1pqm304iujro8">
                  EXAMPLE: Spot the fake
                </p>
                <div className="space-y-3" data-id="element-2243" data-mp="1loirjfw6gn2y">
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-green-200" data-id="element-2244" data-mp="1ygccapnkgu7m">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3" data-id="element-2245" data-mp="23fp8w248pfne" />
                    <span className="font-mono text-sm" data-id="element-2246" data-mp="28e0h68evzi3i">
                      support@amazon.com
                    </span>
                    <span className="ml-auto text-green-600 text-sm font-medium" data-id="element-2247" data-mp="2d5iiixktnkp0">
                      Real
                    </span>
                  </div>
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-red-200" data-id="element-2248" data-mp="2m55aksrnve5e">
                    <span className="text-red-500 mr-3" data-id="element-2249" data-mp="2r48euazthgcb">✗</span>
                    <span className="font-mono text-sm" data-id="element-2250" data-mp="2r51e6qwg53kf">
                      support@amaz0n-secure.com
                    </span>
                    <span className="ml-auto text-red-600 text-sm font-medium" data-id="element-2251" data-mp="wq78l51xtr27">
                      Fake
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-2252" data-mp="u5srd4dagt9x">
                <button className="text-gray-500 font-medium hover:text-[#631bb7] transition-colors" data-id="element-2253" data-mp="u73sewfcnmxc">
                  ← Previous Step
                </button>
                <button className="bg-[#631bb7] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#521099] transition-colors" data-id="element-2254" data-mp="kf6hzrw60yzo">
                  Next Step →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-2255" data-mp="185ts8jtk00pe">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2256" data-mp="136h4zxyrsrma">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2257" data-mp="134w8i2swayls">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-2258" data-mp="y7w2tszyjxv7">
              Your family's gaps are 3 minutes away from being visible.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-2259" data-mp="3nubwyr4n6b89">
              See what's been missed — and get a clear plan for fixing it. Free,
              no email required.
            </p>
            <Link to="/onboarding" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-2260" data-mp="2awbq3lbxcduc">
              Start My Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer data-id="element-2261" data-mp="1r9x6dq4gu7i1" />
    </div>;
}